import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, TrendingUp, Award, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, stats } from '../utils/mockData';

import usePageSeo from '../hooks/usePageSeo';
const Home = () => {
  usePageSeo({
    title: undefined,
    description: 'White Paper Accounts Auditing — UAE audit, tax, accounting, Corporate Tax & VAT advisory. Trusted partner for businesses across Mainland and Free Zones.',
    path: '/'
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-teal-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxNGI4YTYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE2djI4YTQgNCAwIDAgMS00IDRIMTZhNCA0IDAgMCAxLTQtNFYxNmE0IDQgMCAwIDEgNC00aDEyYTQgNCAwIDAgMSA0IDR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full">
                  Trusted UAE Advisory Firm
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your Trusted Partner in{' '}
                <span className="text-teal-600 relative inline-block">
                  Assurance
                  <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" fill="none">
                    <path d="M0 4C50 2 150 2 200 4" stroke="#14b8a6" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </span>{' '}
                & Advisory
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Comprehensive audit, tax, and advisory services at competitive rates. Delivering excellence, integrity, and practical financial solutions for your business growth in the UAE.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg group">
                    Get Free Consultation
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-2 border-gray-300 hover:border-teal-600 hover:text-teal-600 px-8 py-6 text-lg">
                    Explore Services
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-teal-600" size={20} />
                  <span className="text-sm font-medium text-gray-700">100% Compliance</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-teal-600" size={20} />
                  <span className="text-sm font-medium text-gray-700">200+ Clients</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-teal-600" size={20} />
                  <span className="text-sm font-medium text-gray-700">98% Retention</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400/20 to-blue-400/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop&q=75&fm=webp"
                alt="Professional business consulting"
                className="relative rounded-2xl shadow-2xl w-full h-auto"
                fetchPriority="high"
                decoding="async"
                width="800"
                height="600"
              />
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <Award className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">18+</p>
                    <p className="text-sm text-gray-600">Years Combined Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const icons = {
                Users: Users,
                TrendingUp: TrendingUp,
                CheckCircle: CheckCircle,
                Award: Award
              };
              const Icon = icons[stat.icon];
              
              return (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4 group-hover:bg-teal-600 transition-colors">
                    <Icon className="text-teal-600 group-hover:text-white transition-colors" size={28} />
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</p>
                  <p className="text-sm text-gray-600 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Financial Solutions
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From statutory audits to strategic advisory, we provide end-to-end services tailored to your business needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Audit & Assurance', desc: 'Transparent, accurate statutory and internal audits', icon: '📊', link: '/services' },
              { title: 'Tax Consultancy', desc: 'VAT, corporate tax, and strategic tax planning', icon: '💼', link: '/services' },
              { title: 'Accounting Services', desc: 'Complete bookkeeping and financial reporting', icon: '📈', link: '/accounting-services-uae' },
              { title: 'goAML Compliance', desc: 'AML/CFT compliance and reporting for UAE entities', icon: '🛡️', link: '/services/goaml-compliance' }
            ].map((service, index) => (
              <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group cursor-pointer">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.desc}</p>
                  <Link to={service.link} className="text-teal-600 font-semibold flex items-center hover:gap-2 transition-all" aria-label={`Learn more about ${service.title}`}>
                    Learn About {service.title} <ChevronRight size={18} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mt-10" data-testid="home-accounting-note">
            Need reliable books every month? Our{' '}
            <Link to="/accounting-services-uae" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900" data-testid="home-accounting-link">
              accounting and bookkeeping services in the UAE
            </Link>{' '}
            cover cloud bookkeeping, VAT-compliant records, management reports and year-end audit readiness for mainland and free zone companies.
          </p>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-600 to-teal-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose White Paper?</h2>
            <p className="text-teal-100 text-lg max-w-2xl mx-auto">
              We combine competitive pricing with uncompromising quality, delivering exceptional value for businesses across the UAE.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'UAE Expertise', desc: 'Deep knowledge of local regulations and tax landscape' },
              { title: 'Client-Centric', desc: 'Personalized service with dedicated senior professionals' },
              { title: 'Proven Results', desc: '98% client retention with 100% compliance rate' },
              { title: 'Competitive Pricing', desc: 'Premium quality at rates designed for all business sizes' },
              { title: 'Long-term Partnership', desc: 'Building lasting relationships as your trusted advisor' },
              { title: 'Ethics & Integrity', desc: 'Highest standards of professionalism and confidentiality' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all">
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-teal-100">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/why-choose-us">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100">
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Client Success</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say about working with White Paper.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center space-x-3">
                    {testimonial.image ? (
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-teal-600 font-bold text-lg">
                          {testimonial.name ? testimonial.name.charAt(0) : testimonial.role.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      {testimonial.name && <p className="font-semibold text-gray-900">{testimonial.name}</p>}
                      <p className="text-sm text-gray-600">{testimonial.role}, {testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Schedule your free consultation today and discover how we can help your business thrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg">
                Book Free Consultation
              </Button>
            </Link>
            <a href="tel:+971501219341" data-testid="home-call-btn">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                Call Now
              </Button>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="home-whatsapp-btn">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg">
                WhatsApp Chat
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
